import { defineStore } from 'pinia'

import {
    updateRtb, updateRtbMode, getCreateDataModifierAndFilter, generateUrl,
} from '@/api/call-tracking/campaign/campaign-rtb-routes'

import { useDefaultStore } from '@/store/defaultStore'
import { useDetailCampaignStore } from '@/store/call-tracking/campaigns/detailCampaignStore'
import { useDetailCampaignRtbParametersStore } from '@/store/call-tracking/campaigns/detailCampaignRtbParametersStore'
import { useDetailCampaignTrackingNumberTableStore } from '@/store/call-tracking/campaigns/detailCampaignTrackingNumberTable'
import { useAuthorizationStore } from '@/store/authorizationStore'

import { RTB_ROLES } from '@/constants/call-tracking/campaign/rtb/rtbRoles'

export const useDetailCampaignRtbStore = defineStore('detailCampaignRtb', {
    state: () => ({
        updateLoading: false,
        createDataLoading: false,
        urlLoading: false,

        activeOverrideFirstTime: false,

        operators: [],
        operations: [],

        formData: {
            // for override options
            vendor_id: null,
            number_id: null,
            override_enabled: false,

            enabled: null,
            type_id: null,

            // conversion options
            converted_on_option_id: null,
            // caller_id_enabled: null,
            duplicate_option_id: null,
            time_limit: null,
            call_length: null,
            reseller_call_duration_handling_type_id: null,

            // real-time availability options
            real_time_availability_enabled: null,

            // buyer bid expiration
            bid_valid_period: null,
            expired_bid_option_id: null,

            // buyer bid options
            buyer_base_bid: null,
            buyer_max_bid: null,
            buyer_min_bid: null,

            // reseller bid options
            reseller_bid_margin: null,
            reseller_max_bid: null,
            reseller_min_bid: null,
            reseller_loss_bid_enable: null,
            reseller_margin_type_id: null,
            reseller_adjusted_min_bid_margin: null,
        },

        urlData: {},
    }),

    getters: {
        campaignSlug() {
            const detailCampaignStore = useDetailCampaignStore()
            return detailCampaignStore.slug
        },

        isReseller({ formData }) {
            const { type_id } = formData
            return RTB_ROLES.reseller.id === type_id
        },
    },

    actions: {
        parseCreateData(payload) {
            this.operations = payload.operations
            this.operators = payload.operators

            const detailCampaignRtbParametersStore = useDetailCampaignRtbParametersStore()
            detailCampaignRtbParametersStore.setParameters(payload)
        },

        setActiveOverrideFirstTime(value) {
            this.activeOverrideFirstTime = value
        },

        parsePayload(payload) {
            Object.keys(this.formData).forEach((name) => {
                if (payload.hasOwnProperty(name)) {
                    this.formData[name] = payload[name]
                }
            })
        },

        changeRtbMode(payload) {
            const detailCampaignStore = useDetailCampaignStore()

            detailCampaignStore.updateRtbMode(payload.type_id)

            this.formData.type_id = payload.type_id
        },

        async goToUpdateRtb(formDataForUpdate) {
            const defaultStore = useDefaultStore()

            this.updateLoading = true

            const formDataForSend = { ...this.formData, ...formDataForUpdate, webhook: undefined }

            const { success, payload, message } = await updateRtb({
                formData: formDataForSend,
                slug: this.campaignSlug,
            })

            if (success) {
                if (!formDataForSend.vendor_id) {
                    const detailCampaignStore = useDetailCampaignStore()
                    detailCampaignStore.setRtbUpdateData(payload)
                }

                this.parsePayload(payload)

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.updateLoading = false

            return { success, payload }
        },

        async goToUpdateRtbMode(formDataForUpdate) {
            const defaultStore = useDefaultStore()

            this.updateLoading = true

            const { success, payload, message } = await updateRtbMode({
                formData: formDataForUpdate,
                slug: this.campaignSlug,
            })

            if (success) {
                this.changeRtbMode(payload)

                const detailCampaignTrackingNumberTable = useDetailCampaignTrackingNumberTableStore()

                detailCampaignTrackingNumberTable.updateNumbersAfterChangeRtbMode(payload)

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.updateLoading = false

            return { success, payload }
        },

        async goToLoadCreateDataModifierAndFilter() {
            const authorizationStore = useAuthorizationStore()

            if (!!this.operators.length && !!this.operations.length || authorizationStore.isVendor) {
                return
            }

            this.createDataLoading = true

            const { success, payload, message } = await getCreateDataModifierAndFilter(this.campaignSlug)

            if (success) {
                this.parseCreateData(payload)
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }

            this.createDataLoading = false
        },

        async goToGenerateUrl(phoneNumberSlug) {
            this.urlLoading = true

            const defaultStore = useDefaultStore()

            const { success, payload, message } = await generateUrl({
                slug: this.campaignSlug,
                phoneNumberSlug,
            })

            if (success) {
                this.urlData = payload
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.urlLoading = false
        },
    },
})
